import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { GTColumnSelectAnchor, GTExpander, GTFrameworkCell, GTModifierLabel, GTTdm } from 'src/components/GenericTable';
import { formatDate, formatNumber } from 'src/utils/misc';
import { filter } from 'lodash';
import { Link } from 'react-router-dom';
import { GTInternalIds } from 'src/components/GenericTable/types';
import _ from 'lodash';
import { TextOverflowTooltip, getIncompleteStateNew } from 'src/common';
import { MPEvents } from 'src/utils/mixpanel/types';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import useVersion from 'src/hooks/useVersion';
import { encodeQueryParams } from 'src/api/client';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { OpenInNew } from '@mui/icons-material';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { ScenarioQuery, useUnlinkScenarioFromControlsMutation, ScenarioControlsQuery } from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';
import { showSuccessToast, toastMessages } from 'src/components/Toast';

type RiskManagementPlan = NonNullable<ScenarioControlsQuery['scenario']>['riskManagementPlans'][0];

type UseColumns = (a: { data?: RiskManagementPlan[]; scenario: NonNullable<ScenarioQuery['scenario']> }) => {
  columns: ColumnDef<RiskManagementPlan>[];
  GTColumnSelectAnchorExported: JSX.Element;
  editModalId: string | undefined;
  setEditModalId: (id: string | undefined) => void;
};

const defaultColumns = [
  'strategy',
  'name',
  'owner',
  'framework_controls',
  'status',
  'expected_active_date',
  'actual_active_date',
  'updated_at',
  '_mod_applied',
];

export const useColumnsControls: UseColumns = ({ data = [], scenario }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.scenarioControls}-columns`, defaultColumns);
  const [editModalId, setEditModalId] = useState<string | undefined>(undefined);
  const { versionId, assessmentId } = useVersion();
  const { refetchQuery } = useCache();

  const [unlink] = useUnlinkScenarioFromControlsMutation();

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.expander, GTInternalIds.tdm, GTInternalIds.checker];

    const list: ColumnDef<RiskManagementPlan>[] = [
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'ux_id',
        header: 'Id',
        accessorFn: (row) => row.control?.id,
        cell: (info) => info.getValue(),
      },
      {
        id: 'strategy',
        header: 'Strategy',
        accessorFn: (row) => row.control?.strategy,
        cell: (info) => info.getValue(),
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.control?.name,
        cell: (info) => (
          <TextOverflowTooltip>
            <Link
              className="table-link"
              to={`/risk-management/control/${info.row.original.control?.id}${
                versionId ? encodeQueryParams({ version: versionId, assessmentId }) : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: { value: ['Scenario'], params: { id: scenario.id } },
                  destination: { value: ['Control'], params: { id: info.row.original.control?.id } },
                  tags: ['SCENARIO', 'RMP'],
                });
              }}
            >
              {info.getValue() as string}
            </Link>
          </TextOverflowTooltip>
        ),
        sortingFn: sortingFns.text,
      },
      {
        id: 'owner',
        header: 'Owner',
        accessorFn: (row) => row.control?.owner?.text,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'framework_controls',
        header: 'Framework',
        cell: (info) => {
          const control = info.row.original.control;
          return <GTFrameworkCell controls={control?.frameworkControls || []} />;
        },
        sortingFn: sortingFns.text,
      },
      {
        id: 'annual_cost',
        header: 'Annual Cost',
        accessorFn: (row) => row.control?.annualCost,
        cell: ({ row }) => formatNumber(row.original.control?.annualCost, { symbol: true }),
        sortingFn: sortingFns.alphanumeric,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.control?.status,
        cell: (info) => info.getValue(),
      },
      {
        id: 'existingMaturity',
        header: 'Existing Maturity',
        cell: ({ row }) => row.original.control?.existingMaturity,
      },
      {
        id: 'targetMaturity',
        header: 'Target Maturity',
        cell: ({ row }) => row.original.control?.targetMaturity,
      },
      {
        id: 'expected_active_date',
        header: 'Expected Live Date',
        accessorFn: (row) => row.control?.expectedActiveDate,
        cell: ({ row }) => {
          const date = row.original.control?.expectedActiveDate;
          if (!date) return null;
          return <TLLink date={date}>{formatDate(date)}</TLLink>;
        },
      },
      {
        id: 'actual_active_date',
        header: 'Actual Live Date',
        accessorFn: (row) => row.control?.actualActiveDate,
        cell: ({ row }) => {
          const date = row.original.control?.actualActiveDate;
          if (!date) return null;
          return <TLLink date={date}>{formatDate(date)}</TLLink>;
        },
      },
      {
        id: 'updated_at',
        header: 'Last Edited',
        accessorFn: (row) => row.control?.meta?.updatedAt,
        cell: ({ row }) => {
          const date = row.original.control?.meta?.updatedAt;
          if (!date) return null;
          return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
        },
      },
      {
        id: '_mod_applied',
        header: 'Modifiers',
        accessorFn: (row) => row.modifiers?.length || 0,
        cell: (info) => {
          const modifiersAvailable =
            info.row.original.control?.strategy === 'Treat' || info.row.original.control?.strategy === 'Transfer';
          if (!modifiersAvailable) return null;
          return (
            <GTModifierLabel
              length={info.getValue() as number}
              satisfiesModifier={getIncompleteStateNew(scenario) !== 'TECH'}
            />
          );
        },
      },
      {
        id: 'source',
        header: 'Link',
        accessorFn: (row) => row.control?.linkUrl,
        cell: (info) => {
          const url = info.getValue();
          const linkText = (info.row.original.control?.linkText || url || '') as string;

          return url ? (
            <a
              href={url as string}
              target="_blank"
              rel="noopener noreferrer"
              className="table-link"
              onClick={(e) => e.stopPropagation()}
            >
              {linkText}{' '}
              <OpenInNew
                css={`
                  font-size: 14px !important;
                  color: inherit;
                `}
              />
            </a>
          ) : (
            <span>{linkText}</span>
          );
        },
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const onUnlickControl = !versionId
            ? async () => {
                await unlink({ variables: { scenarioId: scenario.id, controlIds: [row.original.control?.id || ''] } });
                await refetchQuery({ include: ['scenario', 'scenarioControls'] });
                showSuccessToast(toastMessages.control.unlinked);
              }
            : undefined;

          const onEditClick = !versionId ? () => setEditModalId(row.original.control?.id) : undefined;

          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm onUnlickControl={onUnlickControl} onEditClick={onEditClick} />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, scenario, versionId, assessmentId]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.scenarioControls}
      options={[
        { key: 'ux_id', title: 'Id' },
        { key: 'strategy', title: 'Strategy' },
        { key: 'name', title: 'Name' },
        { key: 'owner', title: 'Owner' },
        { key: 'framework_controls', title: 'Framework' },
        { key: 'annual_cost', title: 'Annual Cost' },
        { key: 'status', title: 'Status' },
        { key: 'existingMaturity', title: 'Existing Maturity' },
        { key: 'targetMaturity', title: 'Target Maturity' },
        { key: 'expected_active_date', title: 'Expected Live Date' },
        { key: 'actual_active_date', title: 'Actual Live Date' },
        { key: 'updated_at', title: 'Last Edited' },
        { key: '_mod_applied', title: 'Modifiers' },
        { key: 'source', title: 'Link' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
    editModalId,
    setEditModalId,
  };
};
